/**
 * World 8 — "Storm Vault" (Thunder, levels 71–80). ACT II, chapter 3: a
 * Guardian guarding a memory it was begged to forget. Levels/caps stamped from
 * World 8's band (71→80). Thunder pool (Zaricat/Toro — Voltaryx is Clan Z's alone),
 * with at most one outsider (Trixon) per trainer line-up. Finale boss (slot
 * 0) is Toro — the Vault Guardian — who releases the second shard of the First
 * Soul alongside its own Soul Shard (soulSpeciesId: TORO).
 */

import {
  node,
  giftNode,
  giftTracks,
  giftXp,
  rival,
  CAMPAIGN_XP_NORMAL,
  CAMPAIGN_XP_MINI_BOSS,
  CAMPAIGN_XP_ZONE_BOSS,
  ZARICAT,
  TORO,
  VOLTARYX,
  TALION,
  PEFAREON,
  TRIXON,
  type CampaignWorld,
} from './builders.js';
import { GIFT_SKILL_SUIKA } from '../cardShopConfig.js';
import {
  WORLD8_NODE_STORIES as S,
  WORLD8_ZONE_STORIES as Z,
  WORLD8_TAGLINE,
} from './stories/world8.js';

export const WORLD_8: CampaignWorld = {
  worldId: 'w8',
  name: 'Storm Vault',
  element: 'Thunder',
  featuredSpeciesId: TORO,
  story: WORLD8_TAGLINE,
  locked: true,
  zones: [
    {
      zoneId: 'z21',
      name: 'Rising Stairs',
      element: 'Thunder',
      story: Z['z21'],
      nodes: [
        node({
          nodeId: 'z21-n1',
          kind: 'normal',
          rivalName: 'Cliff',
          rivalTitle: 'Bird Keeper',
          ...S['z21-n1'],
          squad: [rival(ZARICAT, 'Latch'), rival(TRIXON, 'Bolt'), rival(TORO, 'Bar')],
          opponentCount: 1,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: ZARICAT,
        }),
        giftNode({
          nodeId: 'z21-g1',
          giver: 'Vault Guard',
          label: 'Trailside Gift',
          story: 'A guard resting on a landing of the stair waves you up.',
          line: 'Long climb, huh? These Soul Shards are for you!',
          reward: { tracks: giftTracks(TORO) },
          portraitSpeciesId: TORO,
        }),
        node({
          nodeId: 'z21-n2',
          kind: 'normal',
          rivalName: 'Vera',
          rivalTitle: 'Channeler',
          ...S['z21-n2'],
          squad: [rival(TRIXON, 'Keepsake'), rival(ZARICAT, 'Token'), rival(TORO, 'Anchor')],
          opponentCount: 1,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: TRIXON,
        }),
        node({
          nodeId: 'z21-n3',
          kind: 'normal',
          rivalName: 'Zeck the Boss',
          rivalTitle: 'Clan Z Boss',
          ...S['z21-n3'],
          // Toro leads instead of the Voltaryx. At 1v1 only slot 0 fights, and this zone sent
          // Zaricat, Voltaryx, Voltaryx, Zaricat, Voltaryx — two faces across five nodes
          // (criterion N5). The third Electric species was already on the bench; it just never
          // came out. Bench order is otherwise untouched. Storm is HELD BACK, not benched: three
          // fight, so Zeck's partner comes in after Cage (docs/campaign/VILLAIN_ESCALATION.md E8).
          squad: [rival(TORO, 'Cage'), rival(VOLTARYX, 'Storm'), rival(ZARICAT, 'Chain')],
          opponentCount: 3,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: TORO,
        }),
        node({
          nodeId: 'z21-nw1',
          kind: 'normal',
          encounterKind: 'wild',
          rivalName: 'Zaricat',
          rivalTitle: 'Wild Suikamon',
          ...S['z21-nw1'],
          squad: [rival(ZARICAT, 'Sparkfang'), rival(TRIXON, 'Jolt'), rival(TORO, 'Rumble')],
          opponentCount: 1,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: ZARICAT,
        }),
        node({
          nodeId: 'z21-n4',
          kind: 'zone_boss',
          rivalName: 'Lock Gate',
          rivalTitle: 'Locked Gate',
          ...S['z21-n4'],
          squad: [rival(TRIXON, 'Deadbolt'), rival(TORO, 'Portcullis'), rival(ZARICAT, 'Ward')],
          opponentCount: 1,
          xpReward: CAMPAIGN_XP_ZONE_BOSS,
          portraitSpeciesId: TRIXON,
        }),
      ],
    },
    {
      zoneId: 'z22',
      name: 'Sealed Halls',
      element: 'Thunder',
      story: Z['z22'],
      nodes: [
        node({
          nodeId: 'z22-n1',
          kind: 'normal',
          rivalName: 'Timmy',
          rivalTitle: 'Youngster',
          ...S['z22-n1'],
          squad: [rival(ZARICAT, 'Index'), rival(TRIXON, 'Folio'), rival(TORO, 'Tome')],
          opponentCount: 2,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: ZARICAT,
        }),
        giftNode({
          nodeId: 'z22-g1',
          giver: 'Old Watchman',
          label: 'Training Charm',
          story: 'A watchman polishing a humming charm holds it up for you.',
          line: 'This charm hums while you train. Take it.',
          reward: { xp: giftXp(7) },
          portraitSpeciesId: TORO,
        }),
        node({
          nodeId: 'z22-n2',
          kind: 'normal',
          rivalName: 'Mimi',
          rivalTitle: 'Lass',
          ...S['z22-n2'],
          squad: [rival(TRIXON, 'Blank'), rival(ZARICAT, 'Echo'), rival(TORO, 'Hollow')],
          opponentCount: 2,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: TRIXON,
        }),
        node({
          nodeId: 'z22-n3',
          kind: 'normal',
          rivalName: 'Lulu',
          rivalTitle: 'Lass',
          ...S['z22-n3'],
          squad: [rival(TORO, 'Creed'), rival(TRIXON, 'Devout'), rival(ZARICAT, 'Doubt')],
          opponentCount: 2,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: TORO,
        }),
        node({
          nodeId: 'z22-n5',
          kind: 'normal',
          rivalName: 'Zip',
          rivalTitle: 'Clan Z',
          ...S['z22-n5'],
          squad: [rival(TALION, 'Dash'), rival(TRIXON, 'Bolt'), rival(ZARICAT, 'Nip')],
          opponentCount: 3,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: TALION,
        }),
        node({
          nodeId: 'z22-nw2',
          kind: 'normal',
          encounterKind: 'wild',
          rivalName: 'Trixon',
          rivalTitle: 'Wild Suikamon',
          ...S['z22-nw2'],
          squad: [rival(TRIXON, 'Arcfang'), rival(ZARICAT, 'Crackle'), rival(TORO, 'Boom')],
          opponentCount: 2,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: TRIXON,
        }),
        node({
          nodeId: 'z22-n4',
          kind: 'zone_boss',
          rivalName: 'Aggie',
          rivalTitle: 'Medium',
          ...S['z22-n4'],
          squad: [rival(TORO, 'Keystone'), rival(TRIXON, 'Rivet'), rival(ZARICAT, 'Hasp')],
          opponentCount: 2,
          xpReward: CAMPAIGN_XP_ZONE_BOSS,
          portraitSpeciesId: TORO,
        }),
      ],
    },
    {
      zoneId: 'z23',
      name: 'Vault Heart',
      element: 'Thunder',
      story: Z['z23'],
      nodes: [
        node({
          nodeId: 'z23-n1',
          kind: 'normal',
          rivalName: 'Sealhand',
          rivalTitle: 'Lock Shade',
          ...S['z23-n1'],
          squad: [rival(TRIXON, 'Regret'), rival(TORO, 'Penance'), rival(ZARICAT, 'Remorse')],
          opponentCount: 3,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: TRIXON,
        }),
        giftNode({
          nodeId: 'z23-g1',
          giver: 'Shrine Keeper',
          label: 'Soul Token',
          story: 'Behind iron bars, a vault shrine glows as you pass.',
          line: 'The vault leaves a big handful of Soul Shards here. Take them!',
          reward: { tracks: giftTracks(TORO, true), suika: GIFT_SKILL_SUIKA },
          portraitSpeciesId: TORO,
        }),
        node({
          nodeId: 'z23-n2',
          kind: 'normal',
          rivalName: 'Vera',
          rivalTitle: 'Channeler',
          ...S['z23-n2'],
          /*
           * **VERA FIELDS TWO, AND THAT IS THE WHOLE OF CRITERION N5's FIX.**
           *
           * Vault Heart is a Thunder zone, and Thunder has exactly THREE species. An
           * elemental world requires every squad pet to match the world's element
           * (`campaignService.test.ts`), exempting only wild encounters and Clan Z — so with
           * five nodes all fielding three-of-three, every line-up in the zone is necessarily
           * the identical set, and no amount of re-casting can produce a second one. That is
           * the 2-distinct-line-up window `campaign-gauntlet` reported at `z23-n1`.
           *
           * `opponentCount` is the dial the element rule does not touch: two of the three is a
           * genuinely different fight to face, and the map already varies this everywhere
           * (49 nodes field one, 54 field two, 48 field three). Zap's Clan Z line-up two nodes
           * later is the zone's other exemption, and between them the window holds three.
           */
          squad: [rival(ZARICAT, 'Solace'), rival(TORO, 'Balm'), rival(TRIXON, 'Numb')],
          opponentCount: 2,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: ZARICAT,
        }),
        node({
          nodeId: 'z23-n3',
          kind: 'mini_boss',
          rivalName: 'Last Lock',
          rivalTitle: 'Last Lock',
          ...S['z23-n3'],
          squad: [rival(TORO, 'Vigil'), rival(TRIXON, 'Sentinel'), rival(ZARICAT, 'Ward')],
          opponentCount: 3,
          xpReward: CAMPAIGN_XP_MINI_BOSS,
          portraitSpeciesId: TORO,
        }),
        node({
          nodeId: 'z23-n5',
          kind: 'normal',
          rivalName: 'Zap',
          rivalTitle: 'Clan Z',
          ...S['z23-n5'],
          squad: [rival(PEFAREON, 'Shade'), rival(TORO, 'Gloom'), rival(TRIXON, 'Dusk')],
          opponentCount: 3,
          xpReward: CAMPAIGN_XP_NORMAL,
          portraitSpeciesId: PEFAREON,
        }),
        node({
          nodeId: 'z23-n4',
          kind: 'zone_boss',
          rivalName: 'Boltheart',
          rivalTitle: 'Vault Guardian',
          ...S['z23-n4'],
          squad: [rival(TORO, 'Boltheart'), rival(TRIXON, 'Stormkin'), rival(ZARICAT, 'Boltkin')],
          opponentCount: 3,
          xpReward: CAMPAIGN_XP_ZONE_BOSS,
          portraitSpeciesId: TORO,
          soulSpeciesId: TORO,
        }),
      ],
    },
  ],
};
